import type { Locale, PayrollClaimSourceKey, PayrollSectionContent } from "./types";

export type PayrollClaimSource = {
  key: PayrollClaimSourceKey;
  label: string;
  sourceFiles: string[];
  publicClaim: Record<Locale, string>;
};

export const payrollClaimSources: PayrollClaimSource[] = [
  {
    key: "employees",
    label: "Employees",
    sourceFiles: [
      "frontend/dm-payroll-modal/src/views/EmployeesView.tsx",
      "frontend/dm-payroll-modal/src/types.ts",
      "app/routes_acct_payroll.py",
      "app/helpers/acct_payroll.py"
    ],
    publicClaim: {
      sv: "Medarbetare hanteras med referenser och maskerade identitetsvärden.",
      en: "Employees are handled through references and masked identity values."
    }
  },
  {
    key: "imports",
    label: "Imports",
    sourceFiles: ["frontend/dm-payroll-modal/src/views/ImportsView.tsx", "frontend/dm-payroll-modal/src/hooks/usePayrollData.ts", "app/routes_acct_payroll.py"],
    publicClaim: {
      sv: "Lönekällor kan importeras innan beräkning.",
      en: "Payroll source rows can be imported before calculation."
    }
  },
  {
    key: "calculations",
    label: "Calculations",
    sourceFiles: ["frontend/dm-payroll-modal/src/views/CalculationsView.tsx", "frontend/dm-payroll-modal/src/types.ts", "app/helpers/acct_payroll.py"],
    publicClaim: {
      sv: "Beräkningar sammanställer bruttolön, skatt, arbetsgivaravgift, nettolön och utbetalningsunderlag.",
      en: "Calculations summarize gross salary, tax, employer fee, net salary, and payout material."
    }
  },
  {
    key: "batches",
    label: "Payroll batches",
    sourceFiles: ["frontend/dm-payroll-modal/src/views/BatchesView.tsx", "frontend/dm-payroll-modal/src/types.ts", "app/routes_acct_payroll.py"],
    publicClaim: {
      sv: "Lönebatcher kan kontrolleras med rader, totalsummor och status.",
      en: "Payroll batches can be checked with line items, totals, and status."
    }
  },
  {
    key: "adjustments",
    label: "Adjustments",
    sourceFiles: ["frontend/dm-payroll-modal/src/views/AdjustmentsView.tsx", "app/helpers/acct_payroll.py", "app/routes_acct_payroll.py"],
    publicClaim: {
      sv: "Justeringar kan skapas och låsas innan ny beräkning.",
      en: "Adjustments can be created and locked before recalculation."
    }
  },
  {
    key: "reimbursements",
    label: "Reimbursements",
    sourceFiles: ["frontend/dm-payroll-modal/src/views/ReimbursementsView.tsx", "app/routes_acct_payroll.py", "app/helpers/acct_payroll.py"],
    publicClaim: {
      sv: "Utlägg hanteras med kvitto, status, godkännande och bokföringsbar justering.",
      en: "Reimbursements are handled with receipt, status, approval, and accounting-ready adjustment."
    }
  },
  {
    key: "taxTables",
    label: "Tax tables",
    sourceFiles: ["frontend/dm-payroll-modal/src/views/TaxTablesView.tsx", "frontend/dm-payroll-modal/src/types.ts", "app/routes_acct_payroll.py"],
    publicClaim: {
      sv: "Skattetabeller visas som underlag för beräkningen; deklarationsansvar utlovas inte.",
      en: "Tax tables are visible as calculation material; filing responsibility is not promised."
    }
  },
  {
    key: "journals",
    label: "Journals",
    sourceFiles: ["frontend/dm-payroll-modal/src/views/JournalsView.tsx", "app/helpers/acct_payroll.py", "app/routes_acct_payroll.py"],
    publicClaim: {
      sv: "Journalförhandsvisningar kan kontrolleras mot hash innan batchen godkänns.",
      en: "Journal previews can be checked against a hash before batch approval."
    }
  },
  {
    key: "payoutDrafts",
    label: "Payout drafts",
    sourceFiles: ["frontend/dm-payroll-modal/src/views/PayoutDraftsView.tsx", "frontend/dm-payroll-modal/src/api.ts", "app/routes_acct_payroll.py"],
    publicClaim: {
      sv: "Utbetalningsutkast exporteras som filer; inga liveutbetalningar körs från den publika webbplatsen.",
      en: "Payout drafts export as files; no live payouts run from the public website."
    }
  },
  {
    key: "reports",
    label: "Reports",
    sourceFiles: ["frontend/dm-payroll-modal/src/views/ReportsView.tsx", "frontend/dm-payroll-modal/src/types.ts", "app/routes_acct_payroll.py"],
    publicClaim: {
      sv: "Lönerapportartefakter kan genereras och spåras som metadata.",
      en: "Payroll report artifacts can be generated and tracked as metadata."
    }
  },
  {
    key: "redaction",
    label: "Sensitive-data rules",
    sourceFiles: ["frontend/dm-payroll-modal/src/api.ts", "app/routes_acct_payroll.py", "app/helpers/acct_payroll.py"],
    publicClaim: {
      sv: "Råa personidentifierare ska inte exponeras i publik text, felmeddelanden eller evidens.",
      en: "Raw personal identifiers must not appear in public copy, error messages, or evidence."
    }
  }
];

export const payrollContent = {
  sv: {
    id: "payroll",
    eyebrow: "Lön",
    title: "Löneprocess med import, beräkning, batchkontroll och rapportunderlag",
    lead:
      "DynamicBooks visar löneflödet som en kontrollerad arbetsyta för import, beräkning, utlägg, journalförhandsvisning, utbetalningsutkast och rapportartefakter.",
    boundary:
      "Publik text beskriver programvarufunktioner och spårbart underlag. Skattedeklaration, myndighetsinlämning, arbetsgivaransvar och löneservice fastställs separat efter omfattningskontroll.",
    tierNote:
      "Payroll dokumenteras publikt som plattformskapacitet. Basic styr payroll-ytor till uppgraderingspromptar; Business ger payroll-översikt för planering och kontroll; Premium aktiverar payroll processing med fler kontroller, rapportartefakter och journalspår.",
    workflows: [
      {
        id: "payroll-employees",
        title: "Medarbetare och referenser",
        body: "Skapa och kontrollera lönepersoner med anställningsstatus, lönetyp och skattetabell.",
        detail: "Identitet visas som maskerade referenser och ska inte återges som råa identitetsnummer.",
        assetId: "payroll-employees",
        sourceKeys: ["employees", "redaction"]
      },
      {
        id: "payroll-imports",
        title: "Import av lönekällor",
        body: "Importera strukturerade rader för vald period innan lönen beräknas.",
        detail: "Importflödet sparar radantal, adapter, status och källhash för spårbarhet.",
        assetId: "payroll-imports",
        sourceKeys: ["imports", "redaction"]
      },
      {
        id: "payroll-calculations",
        title: "Beräkningar",
        body: "Kör beräkning för perioden och kontrollera bruttolön, skatt, arbetsgivaravgift och nettobelopp.",
        detail: "Varningar, totalsummor och raddetaljer hålls spårbara innan batchen fortsätter.",
        assetId: "payroll-calculations",
        sourceKeys: ["calculations", "taxTables", "adjustments"]
      },
      {
        id: "payroll-batches",
        title: "Batchkontroll",
        body: "Samla beräknade rader i en lönebatch med status, totalsummor och period.",
        detail: "Batchen kan öppnas för detaljer, journalförhandsvisning och vidare utbetalningsutkast.",
        assetId: "payroll-batches",
        sourceKeys: ["batches", "journals", "payoutDrafts"]
      },
      {
        id: "payroll-reimbursements",
        title: "Utlägg och kvitton",
        body: "Hantera utlägg med typ, moms, status, kvitto och kontrollhändelser.",
        detail: "Godkända utlägg kan bli låsta lönejusteringar; manuellt betalt läge kör ingen liveutbetalning.",
        assetId: "payroll-reimbursements",
        sourceKeys: ["reimbursements", "adjustments"]
      },
      {
        id: "payroll-journals",
        title: "Journalförhandsvisning",
        body: "Förbered bokföringsrader för lön, skatt, arbetsgivaravgift och nettolön.",
        detail: "Förhandsvisningen har hash och debet/kreditkontroll innan batchgodkännande.",
        assetId: "payroll-journals",
        sourceKeys: ["journals", "batches"]
      },
      {
        id: "payroll-payouts",
        title: "Utbetalningsutkast",
        body: "Exportera utbetalningsutkast som CSV för vald batch.",
        detail: "Flödet är uttryckligen ett utkast och utför inte livebetalningar.",
        assetId: "payroll-payouts",
        sourceKeys: ["payoutDrafts"]
      },
      {
        id: "payroll-reports",
        title: "Rapporter och artefakter",
        body: "Generera och följ lönerapportartefakter kopplade till körning eller batch.",
        detail: "Rapportmetadata visar typ, filnamn, hash och skapandetid utan råa identitetsvärden.",
        assetId: "payroll-reports",
        sourceKeys: ["reports", "redaction"]
      }
    ]
  },
  en: {
    id: "payroll",
    eyebrow: "Payroll",
    title: "Payroll processing with imports, calculations, batch checks, and report material",
    lead:
      "DynamicBooks presents payroll as a controlled workspace for imports, calculations, reimbursements, journal previews, payout drafts, and report artifacts.",
    boundary:
      "Public copy describes software features and traceable material. Tax filing, authority submission, employer responsibility, and payroll service scope are confirmed separately.",
    tierNote:
      "Payroll is documented publicly as platform capability. Basic routes payroll surfaces to upgrade prompts; Business provides payroll overview for planning and checks; Premium enables payroll processing with deeper controls, report artifacts, and journal trail.",
    workflows: [
      {
        id: "payroll-employees",
        title: "Employees and references",
        body: "Create and check payroll employees with employment status, salary type, and tax table.",
        detail: "Identity is displayed as masked references and must not be reproduced as raw personal identifiers.",
        assetId: "payroll-employees",
        sourceKeys: ["employees", "redaction"]
      },
      {
        id: "payroll-imports",
        title: "Payroll source imports",
        body: "Import structured rows for the selected period before payroll is calculated.",
        detail: "The import flow records row count, adapter, status, and source hash for traceability.",
        assetId: "payroll-imports",
        sourceKeys: ["imports", "redaction"]
      },
      {
        id: "payroll-calculations",
        title: "Calculations",
        body: "Run period calculation and check gross salary, tax, employer fee, and net amounts.",
        detail: "Warnings, totals, and line details remain traceable before the batch moves forward.",
        assetId: "payroll-calculations",
        sourceKeys: ["calculations", "taxTables", "adjustments"]
      },
      {
        id: "payroll-batches",
        title: "Batch checks",
        body: "Collect calculated rows in a payroll batch with status, totals, and period context.",
        detail: "The batch can open details, journal preview, and payout drafts.",
        assetId: "payroll-batches",
        sourceKeys: ["batches", "journals", "payoutDrafts"]
      },
      {
        id: "payroll-reimbursements",
        title: "Reimbursements and receipts",
        body: "Handle reimbursements with type, VAT, status, receipt, and control events.",
        detail: "Approved reimbursements can become locked payroll adjustments; manual paid state records status only.",
        assetId: "payroll-reimbursements",
        sourceKeys: ["reimbursements", "adjustments"]
      },
      {
        id: "payroll-journals",
        title: "Journal preview",
        body: "Prepare accounting rows for salary, tax, employer fee, and net salary.",
        detail: "The preview has a hash and debit/credit check before batch approval.",
        assetId: "payroll-journals",
        sourceKeys: ["journals", "batches"]
      },
      {
        id: "payroll-payouts",
        title: "Payout drafts",
        body: "Export payout drafts as CSV for the selected batch.",
        detail: "The workflow is explicitly a draft and does not initiate payment execution.",
        assetId: "payroll-payouts",
        sourceKeys: ["payoutDrafts"]
      },
      {
        id: "payroll-reports",
        title: "Reports and artifacts",
        body: "Generate and track payroll report artifacts linked to a run or batch.",
        detail: "Report metadata shows type, filename, hash, and creation time without raw identity values.",
        assetId: "payroll-reports",
        sourceKeys: ["reports", "redaction"]
      }
    ]
  }
} satisfies Record<Locale, PayrollSectionContent>;
